<script setup lang="ts">
import merge from "lodash/merge";
import type { ModalProps } from "naive-ui";
import { NModal } from "naive-ui";
import { computed } from "vue";

const props = defineProps<{
    danger?: boolean;
    title: string;
    content: string | (() => import("vue").VNodeChild);
    positiveText: string;
    negativeText: string;
    themeOverrides?: NonNullable<ModalProps["themeOverrides"]>;
}>();

const emit = defineEmits(["positive-click", "negative-click"]);

function onPositiveClick() {
    emit("positive-click");
}

function onNegativeClick() {
    emit("negative-click");
}

const modalCustomTheme: NonNullable<ModalProps["themeOverrides"]> = {
    peers: {
        Dialog: {
            borderRadius: "20px",
            iconColor: !props.danger ? "rgb(29, 28, 61)" : "rgb(255, 0, 0)",
            peers: {
                Button: {
                    borderRadiusSmall: "20px",
                    // Primary button
                    borderPrimary: !props.danger
                        ? "1px solid rgb(29, 28, 61)"
                        : "1px solid rgb(255, 0, 0)",
                    colorPrimary: !props.danger ? "rgb(29, 28, 61)" : "rgb(255, 0, 0)",
                    colorHoverPrimary: !props.danger ? "#4b5563" : "#ff4d4d",
                    colorFocusPrimary: !props.danger ? "rgb(61,60,129)" : "rgb(204,0,0)",
                    colorPressedPrimary: !props.danger ? "rgb(39,38,80)" : "rgb(153,0,0)",
                    borderHoverPrimary: !props.danger ? "rgb(29, 28, 61)" : "rgb(255, 0, 0)",
                    borderPressedPrimary: !props.danger ? "rgb(39,38,80)" : "rgb(153,0,0)",
                    borderFocusPrimary: !props.danger
                        ? "1px solid rgb(29, 28, 61)"
                        : "1px solid rgb(255, 0, 0)",
                    // Default button
                    borderHover: "1px solid rgb(232, 234, 226)",
                    textColorHover: "rgb(29, 28, 61)",
                    textColorPressed: "rgb(29, 28, 61)",
                    textColorFocus: "rgb(29, 28, 61)",
                    textColorTextHover: "rgb(29, 28, 61)",
                    textColorGhostHover: "rgb(29, 28, 61)",
                    textColorGhostPressed: "rgb(29, 28, 61)",
                    borderPressed: "1px solid rgb(29, 28, 61)",
                    borderFocus: "1px solid rgb(29, 28, 61)",
                },
            },
        },
    },
};

const mergedThemeOverrides = computed(() => {
    return merge({}, modalCustomTheme, props.themeOverrides);
});
</script>

<template>
    <n-modal
        :show="true"
        preset="confirm"
        :title="title"
        :content="content"
        :positive-text="positiveText"
        :negative-text="negativeText"
        :theme-overrides="mergedThemeOverrides"
        @positive-click="onPositiveClick"
        @negative-click="onNegativeClick"
        @close="onNegativeClick"
    />
</template>
